// repo/primary-care-connect/frontend/src/api/approvals.ts

export type ApprovalRoleName =
  | "SuperAdmin"
  | "SystemAdmin"
  | "Admin"
  | "RegionAdmin"
  | "OhtAdmin"
  | "CommunityNavigator"
  | "ClinicAdmin"
  | "Specialist"
  | "FamilyDoctor";

export type ApprovalViewerRole = {
  roleName: ApprovalRoleName | string;
  clinicId: number | null;
  regionId: number | null;
};

export type ApprovalsPendingViewer = {
  userId: number;
  email: string | null;
  roles: ApprovalViewerRole[];
};

export type ClinicAdminAction = "Add" | "Replace" | string | null;

export type ApprovalStatus = "Pending" | "Approved" | "Rejected" | "Cancelled";

export type DbBoolean = boolean | number | string | null;

export type ApprovalPendingItem = {
  accessRequestId: number;
  userId: number;
  email: string | null;
  displayName: string | null;

  requestedRole: ApprovalRoleName | string;

  // Existing clinic scope when provided.
  clinicId: number | null;

  // Legacy field kept for backward compatibility.
  regionId: number | null;

  // v1 Clinic Admin requester identity collected at request time.
  requesterFirstName?: string | null;
  requesterLastName?: string | null;
  requesterPhone?: string | null;

  // v1 Clinic Admin request details.
  // Existing clinic request: Add / Replace.
  // New clinic request: usually null.
  clinicAdminAction?: ClinicAdminAction;

  // New clinic draft fields when clinicId is null.
  newClinicName?: string | null;
  newClinicAddressLine1?: string | null;
  newClinicAddressLine2?: string | null;
  newClinicCityId?: number | null;
  newClinicCityName?: string | null;
  newClinicProvince?: string | null;
  newClinicPostalCode?: string | null;
  newClinicPhone?: string | null;
  newClinicWebsite?: string | null;

  // Existing clinic context when clinicId is provided.
  existingClinicName?: string | null;
  existingClinicGovernanceStatus?: string | null;
  existingClinicAddressLine1?: string | null;
  existingClinicAddressLine2?: string | null;
  existingClinicCityId?: number | null;
  existingClinicCityName?: string | null;
  existingClinicPostalCode?: string | null;
  existingClinicPhone?: string | null;
  existingClinicWebsite?: string | null;

  // Useful UI field: number of currently active clinic admins.
  existingClinicActiveAdminCount?: number | null;

  // PIA / legal acceptance metadata.
  // Backend may return either boolean/bit-like flags or timestamp-only evidence.
  attestationAccepted?: DbBoolean;
  attestationAcceptedAt?: string | null;
  termsAccepted?: DbBoolean;
  termsAcceptedAt?: string | null;
  clinicAdminAgreementAccepted?: DbBoolean;
  clinicAdminAgreementAcceptedAt?: string | null;

  // Governance review metadata.
  reviewedByUserId?: number | null;
  reviewedAt?: string | null;
  decisionNote?: string | null;

  // Legacy physician field; expected null for ClinicAdmin requests.
  cpso: string | null;

  // Backend-calculated self-request flag.
  // Used to prevent self-approval in Super Admin UI.
  isSelfRequest?: boolean;

  // Backend + DB use Rejected, not Declined.
  status: ApprovalStatus | string;
  submittedAt: string; // ISO
};

export type ApprovalsPendingResponse = {
  viewer: ApprovalsPendingViewer;
  items: ApprovalPendingItem[];
};

// Backend accepts "approve" / "decline" case-insensitively.
// Backend maps "decline" -> Rejected to match DB constraint.
export type DecideApprovalDecision = "approve" | "decline";

export type DecideApprovalResponse = {
  accessRequestId: number;
  decision: "Approved" | "Rejected";
  reviewedByUserId: number;
  clinicId: number | null;
  createdClinicId: number | null;
  geocode?: {
    ok: boolean;
  };
};

async function readJsonSafe(res: Response) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

// v1 locked endpoint
export async function getApprovalsPending(): Promise<ApprovalsPendingResponse> {
  const res = await fetch("/api/approvals-pending", {
    method: "GET",
    credentials: "include",
  });

  const data = await readJsonSafe(res);

  if (!res.ok) {
    const msg =
      data?.error ||
      data?.message ||
      `Failed to load approvals (${res.status})`;
    throw new Error(msg);
  }

  return data as ApprovalsPendingResponse;
}

// v1 locked endpoint
export async function decideApproval(
  accessRequestId: number,
  decision: DecideApprovalDecision,
  note?: string
): Promise<DecideApprovalResponse> {
  // POST /api/approvals/{accessRequestId}/decide
  const res = await fetch(`/api/approvals/${accessRequestId}/decide`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      decision,
      decisionNote: String(note || "").trim() || null,
    }),
  });

  const data = await readJsonSafe(res);

  if (!res.ok) {
    const msg =
      data?.error || data?.message || `Decision failed (${res.status})`;
    throw new Error(msg);
  }

  return data as DecideApprovalResponse;
}